import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  ChargeCardPaymentCopyDetail,
  Mutation,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import usePaymentHistory from 'features/believe/PaymentHistory/hook/usePaymentHistory';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import CancelChargeCardPayment from 'services/graphql/queries/MutationCancelChargeCardPayment';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useMutation } from 'urql';

const PaymentHistoryDetailsScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { id } = useLocalSearchParams();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'paymentHistoryDetails',
  });
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { error, loading, paymentList } = usePaymentHistory();

  const [{ data, fetching }, cancelChargeCardPayment] = useMutation<Mutation>(
    CancelChargeCardPayment,
  );
  const cancelPaymentStatus = data?.cancelChargeCardPayment?.__typename;

  useEffect(() => {
    if (cancelPaymentStatus === Typenames.CancelChargeCardPaymentSuccess) {
      router.back();
    }
  }, [cancelPaymentStatus, id, router]);

  const handleCancelButton = () => {
    bottomSheetRef?.current?.present();
  };

  const handleBottomSheetCancelButton = async () => {
    bottomSheetRef?.current?.close();
    void (await cancelChargeCardPayment({
      id,
    }));
  };

  const paymentListData = paymentList?.filter((item) => item.id === id) || [];
  const detailsData = paymentListData[0]
    ?.details as ChargeCardPaymentCopyDetail[];

  const statusObject = detailsData?.find((item) => item?.title === 'Status');
  const shouldShowCancelButton = statusObject?.copy === 'Pending';

  const handleBackButton = () => {
    router.back();
    bottomSheetRef?.current?.close();
  };
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('screenHeader'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading || fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ||
          cancelPaymentStatus === Typenames.CancelChargeCardPaymentError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: translatedText('tryAgain'),
                onPress: handleBackButton,
              }}
              screenAnalyticsEventKey="rewardsError"
            />
          ) : null
        }
      />
      {detailsData && detailsData.length > 0 ? (
        <View style={styles.scrollViewStyle}>
          <View style={styles.amountContainer}>
            <ExpressiveSmText numberOfLines={1}>
              {detailsData?.[1]?.copy}
            </ExpressiveSmText>
          </View>
          {detailsData.map((item, index) => (
            <View key={item?.id} style={styles.cardOuterContainer}>
              <View style={styles.cardInnerContainer}>
                <BodyXSText numberOfLines={1}>{item?.title ?? ''}</BodyXSText>
                <BodyBaseText numberOfLines={1}>
                  {item?.copy as string}
                </BodyBaseText>
              </View>
              {index !== detailsData.length - 1 ? <HairLineDivider /> : null}
            </View>
          ))}
          {shouldShowCancelButton ? (
            <View style={styles.buttonStyle}>
              <Button
                label={translatedText('cancelButton')}
                onPress={handleCancelButton}
                size="LARGE"
                type="SECONDARY"
                width="FULL"
              />
            </View>
          ) : null}
        </View>
      ) : null}
      <BottomSheet ref={bottomSheetRef}>
        <View style={styles.bottomSheetStyle}>
          <TitleMdText numberOfLines={2}>
            {translatedText('bottomSheet.heading')}
          </TitleMdText>
          <BodyBaseText style={styles.descriptionStyle} numberOfLines={2}>
            {translatedText('bottomSheet.description')}
          </BodyBaseText>
          <View style={styles.buttonContainer}>
            <Button
              label={translatedText('bottomSheet.bottomSheetCancelButton')}
              onPress={handleBottomSheetCancelButton}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
            <Button
              label={translatedText('bottomSheet.bottomSheetBackButton')}
              onPress={handleBackButton}
              size="LARGE"
              type="SECONDARY"
              width="FULL"
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  amountContainer: {
    alignItems: 'center',
    paddingBottom: theme.spacing.l,
    paddingTop: theme.spacing.l,
  },
  bottomSheetStyle: {
    padding: theme.spacing.m,
  },
  buttonContainer: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  buttonStyle: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: theme.spacing.m,
  },
  cardInnerContainer: {
    paddingVertical: theme.spacing.m,
  },
  cardOuterContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  descriptionStyle: {
    marginVertical: theme.spacing.s,
  },
  scrollViewStyle: {
    flex: 1,
  },
});

export default PaymentHistoryDetailsScreen;
